'use client';

import { Content, isFilled } from '@prismicio/client';
import { PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';
import { useForm, SubmitHandler } from 'react-hook-form';

// Define the form data structure
type FormData = {
  [key: string]: string;
};

// Define the props for the component
export type CtaWithFormProps = SliceComponentProps<Content.CtaWithFormSlice>;

const CtaWithForm = ({ slice }: CtaWithFormProps): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit: SubmitHandler<FormData> = (data) => {
    // Handle form submission logic here (e.g., send to an API)
    console.info('Form data:', data);
  };

  const renderField = (
    item: Content.CtaWithFormSliceTextLeftFormRightPrimaryFormFieldsItem,
  ) => {
    const fieldId = item.field_label?.toLowerCase().replace(/\s+/g, '_') || '';

    switch (item.field_type) {
      case 'text':
        return (
          <input
            {...register(fieldId, { required: item.field_required || false })}
            placeholder={item.field_label || ''}
            className="input-style"
          />
        );
      case 'email':
        return (
          <input
            type="email"
            {...register(fieldId, { required: item.field_required || false })}
            placeholder={item.field_label || ''}
            className="input-style"
          />
        );
      case 'textarea':
        return (
          <textarea
            {...register(fieldId, { required: item.field_required || false })}
            placeholder={item.field_label || ''}
            className="input-style h-24"
          />
        );
      case 'select':
        return (
          <select
            {...register(fieldId, { required: item.field_required || false })}
            className="input-style select-style"
          >
            <option value="">{item.field_label}</option>
            {item.field_options?.split(',').map((opt: string) => (
              <option key={opt} value={opt.trim()}>
                {opt.trim()}
              </option>
            ))}
          </select>
        );
      default:
        return null;
    }
  };

  return (
    <section className="bg-gradient-to-b from-[#0E0F0F] to-[#012A24] text-white py-24">
      <div className="container mx-auto grid lg:grid-cols-2 gap-16 items-center px-6">
        {/* Left Column: Text Content */}
        <div className="flex flex-col gap-8">
          <div className="max-w-xl">
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-5xl font-bold">{children}</h2>
                ),
              }}
            />
          </div>
          <div className="max-w-xl text-lg text-white/80">
            <PrismicRichText field={slice.primary.description} />
          </div>
          {isFilled.link(slice.primary.primary_button) && (
            <PrismicNextLink
              field={slice.primary.primary_button}
              className="secondary-button max-w-fit"
            >
              <ArrowRightIcon className="arrow-icon" />
              Let&apos;s Talk
            </PrismicNextLink>
          )}
        </div>

        {/* Right Column: Form */}
        <div className="glassy-container p-8 rounded-2xl">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            {slice.primary.form_fields.map((item, index) => (
              <div key={index} className="flex flex-col">
                {renderField(item)}
                {errors[
                  item.field_label?.toLowerCase().replace(/\s+/g, '_') || ''
                ] && (
                  <span className="text-red-500 text-sm mt-1">
                    This field is required
                  </span>
                )}
              </div>
            ))}
            <button type="submit" className="secondary-button w-full">
              <ArrowRightIcon className="arrow-icon" />
              {slice.primary.form_submit_text || 'Send'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CtaWithForm;
